import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, ChevronRight, Tv, MessageCircle, ClipboardList, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ExperienceToggle } from '@/components/ExperienceToggle';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import type { ExperienceMode } from '@/hooks/usePreferredSport';

type Step = 'welcome' | 'sport' | 'experience' | 'persona' | 'quiz' | 'goals' | 'saving';

const PERSONAS = [
  { id: 'fan', emoji: '📺', label: 'Fan', description: 'I watch games and want to understand what\'s happening' },
  { id: 'parent', emoji: '👨‍👧‍👦', label: 'Parent', description: 'My kid plays and I want to follow along' },
  { id: 'player', emoji: '🏃', label: 'Player / Athlete', description: 'I play the sport and want to improve my IQ' },
  { id: 'coach', emoji: '📋', label: 'Coach', description: 'I coach or want to learn schemes and strategy' },
];

const SPORT_OPTIONS = [
  { id: 'football', emoji: '🏈', label: 'Football', description: 'NFL plays, formations, and strategy' },
  { id: 'soccer', emoji: '⚽', label: 'Soccer', description: 'Tactics, formations, and set pieces' },
  { id: 'basketball', emoji: '🏀', label: 'Basketball', description: 'Pick & roll, zone defense, and offensive sets' },
  { id: 'baseball', emoji: '⚾', label: 'Baseball', description: 'Innings, pitch counts, and situational baseball' },
];

interface QuizQuestion {
  question: string;
  options: { label: string; points: number }[];
}

const FOOTBALL_QUESTIONS: QuizQuestion[] = [
  {
    question: 'A defense is in "Cover 3." What does that tell you about the deep part of the field?',
    options: [
      { label: 'Three defenders each cover a deep third of the field', points: 2 },
      { label: 'Three linebackers drop into man coverage on the receivers', points: 0 },
      { label: 'The strong safety blitzes while two corners play deep halves', points: 0 },
    ],
  },
  {
    question: 'The offense lines up in "21 personnel." What does that mean?',
    options: [
      { label: '2 wide receivers, 1 tight end, and 2 running backs', points: 0 },
      { label: '2 running backs, 1 tight end, and 2 wide receivers', points: 2 },
      { label: '2 tight ends, 1 running back, and 2 wide receivers', points: 0 },
    ],
  },
  {
    question: 'Pre-snap, the QB sees a single-high safety. What coverage family does this most likely indicate?',
    options: [
      { label: 'Cover 2 or Cover 4 — two deep safeties splitting the field', points: 0 },
      { label: 'Cover 1 or Cover 3 — one safety playing center field', points: 2 },
      { label: 'Cover 0 — no deep safety, with all-out man pressure', points: 0 },
    ],
  },
  {
    question: 'On an RPO, the quarterback reads a specific defender after the snap. Why?',
    options: [
      { label: 'To decide whether to hand off or throw based on that defender\'s reaction', points: 2 },
      { label: 'To identify the mike linebacker for the offensive line\'s protection', points: 0 },
      { label: 'To call an audible and change the play at the line of scrimmage', points: 0 },
    ],
  },
  {
    question: 'A receiver runs a "post" route. Which way does the route break?',
    options: [
      { label: 'Upfield then sharply toward the sideline at a 45° angle', points: 0 },
      { label: 'Upfield then diagonally toward the middle of the field', points: 2 },
      { label: 'A short out-cut then vertically up the sideline', points: 0 },
    ],
  },
];

const SOCCER_QUESTIONS: QuizQuestion[] = [
  {
    question: 'A team is "pressing high." What is the primary tactical objective?',
    options: [
      { label: 'Win the ball near the opponent\'s goal by closing down passing lanes quickly', points: 2 },
      { label: 'Push the defensive line up to catch attackers offside more often', points: 0 },
      { label: 'Keep possession by recycling the ball in the opponent\'s half', points: 0 },
    ],
  },
  {
    question: 'In a 4-2-3-1 formation, what role do the "2" typically fill?',
    options: [
      { label: 'Two center-backs who split wide to create passing angles in build-up', points: 0 },
      { label: 'Two central midfielders who shield the defense and control distribution', points: 2 },
      { label: 'Two wing-backs who provide width and push high in attack', points: 0 },
    ],
  },
  {
    question: 'A "false 9" drops deep into midfield. What tactical problem does this create for the defense?',
    options: [
      { label: 'It overloads the wing to create 2v1 situations out wide', points: 0 },
      { label: 'Center-backs are left marking empty space, opening a gap between the defensive and midfield lines', points: 2 },
      { label: 'It forces the keeper off the line to act as a sweeper behind the defense', points: 0 },
    ],
  },
  {
    question: 'A fullback "inverts" during build-up play. Where do they move?',
    options: [
      { label: 'They push high to overlap the winger along the touchline', points: 0 },
      { label: 'They drop between the center-backs to form a back three', points: 0 },
      { label: 'They tuck inside into central midfield to add passing options', points: 2 },
    ],
  },
  {
    question: 'What is the main tactical advantage of playing out from the back instead of going long?',
    options: [
      { label: 'It can draw the opposition press out of shape, creating space to play through', points: 2 },
      { label: 'It prevents the opposition from winning aerial duels in midfield', points: 0 },
      { label: 'It lets the goalkeeper dictate tempo as the team\'s deepest playmaker', points: 0 },
    ],
  },
];

const BASKETBALL_QUESTIONS: QuizQuestion[] = [
  {
    question: 'A team runs a pick & roll and the defender "drops." What is the offensive player\'s best counter?',
    options: [
      { label: 'Pull up for a mid-range jumper in the space the dropping big leaves open', points: 2 },
      { label: 'Pass the ball to the corner and reset the play from scratch', points: 0 },
      { label: 'Drive baseline away from the screen since the big is dropping back', points: 0 },
    ],
  },
  {
    question: 'A defense switches a ball screen, leaving a guard on a center. How should the offense exploit this?',
    options: [
      { label: 'Run another ball screen immediately to create a new switch', points: 0 },
      { label: 'Post up the center on the smaller guard for an easy inside score', points: 2 },
      { label: 'Have the center step out to the three-point line to shoot over the guard', points: 0 },
    ],
  },
  {
    question: 'Why is the "high post" (free-throw line area) considered the soft spot of a 2-3 zone?',
    options: [
      { label: 'It\'s the closest spot to the basket that\'s unguarded in a 2-3 zone', points: 0 },
      { label: 'A player there can see the whole floor and force two defenders to react, opening passing lanes to cutters or shooters', points: 2 },
      { label: 'Zone defenders aren\'t allowed to guard the free-throw line area', points: 0 },
    ],
  },
  {
    question: 'In transition, you have a 3-on-2 fast break. What\'s the ball handler\'s primary read?',
    options: [
      { label: 'Attack the middle to draw both defenders, then pass to whichever wing is left open', points: 2 },
      { label: 'Pull up for a three-pointer before the defense sets up', points: 0 },
      { label: 'Slow down and wait for the rest of the team to get back on offense', points: 0 },
    ],
  },
  {
    question: 'A team runs "5-out" spacing. What tactical advantage does this create?',
    options: [
      { label: 'It puts all five players outside the arc, maximizing driving lanes and forcing one-on-one defense', points: 2 },
      { label: 'It ensures at least two players are always near the basket for offensive rebounds', points: 0 },
      { label: 'It prevents the defense from pressing full court by spreading players wide', points: 0 },
    ],
  },
];

type Tier = 'easy' | 'medium' | 'hard';

const BASEBALL_POOL: Record<Tier, QuizQuestion[]> = {
  easy: [
    {
      question: 'How many outs end a half-inning?',
      options: [
        { label: 'Three', points: 2 },
        { label: 'Two', points: 0 },
        { label: 'Four', points: 0 },
      ],
    },
    {
      question: 'A batter gets four balls before three strikes. What happens?',
      options: [
        { label: 'They walk to first base', points: 2 },
        { label: 'They strike out', points: 0 },
        { label: 'The count resets', points: 0 },
      ],
    },
    {
      question: 'Which fielder stands behind home plate?',
      options: [
        { label: 'The catcher', points: 2 },
        { label: 'The shortstop', points: 0 },
        { label: 'The third baseman', points: 0 },
      ],
    },
  ],
  medium: [
    {
      question: 'Runner on 3rd, less than 2 outs, fly ball to deep right. What does the runner do?',
      options: [
        { label: 'Tag up after the catch and try to score', points: 2 },
        { label: 'Run on contact regardless', points: 0 },
        { label: 'Stay at 3rd to avoid a double play', points: 0 },
      ],
    },
    {
      question: 'A pitcher throws a 2-seam fastball. What does it typically do?',
      options: [
        { label: 'Runs and sinks toward the pitcher\'s arm side', points: 2 },
        { label: 'Rises straight through the zone', points: 0 },
        { label: 'Breaks sharply down and away like a curveball', points: 0 },
      ],
    },
    {
      question: 'Why might a manager call for an infield shift against a pull hitter?',
      options: [
        { label: 'To position fielders where the batter most often hits the ball', points: 2 },
        { label: 'To intimidate the hitter into bunting', points: 1 },
        { label: 'To rest the shortstop', points: 0 },
      ],
    },
  ],
  hard: [
    {
      question: 'Runner on 1st, no outs. The batter bunts toward 1st. What is the smart defensive play?',
      options: [
        { label: 'Field it and throw to 2nd to get the lead runner', points: 2 },
        { label: 'Always take the easy out at 1st', points: 1 },
        { label: 'Hold the ball and check the runner back', points: 0 },
      ],
    },
    {
      question: 'What does an OPS of .900+ generally indicate about a hitter?',
      options: [
        { label: 'Elite offensive production combining on-base and slugging', points: 2 },
        { label: 'Average contact hitter with low power', points: 0 },
        { label: 'A pitcher\'s batting line', points: 0 },
      ],
    },
    {
      question: 'Bases loaded, 1 out, sharp grounder to the shortstop. What is the highest-percentage play?',
      options: [
        { label: 'Step on 2nd then throw to 1st for the inning-ending double play', points: 2 },
        { label: 'Throw home to force the lead runner', points: 1 },
        { label: 'Run the ball to 3rd for the tag', points: 0 },
      ],
    },
  ],
};

const BASEBALL_QUIZ_LENGTH = 5;

function nextBaseballTier(current: Tier, lastPoints: number): Tier {
  if (lastPoints >= 2) return current === 'easy' ? 'medium' : 'hard';
  if (lastPoints === 0) return current === 'hard' ? 'medium' : 'easy';
  return current;
}


const GOALS = [
  { id: 'watch-smarter', label: 'Watch Smarter', description: 'Understand formations, play calls, and strategy on TV', icon: Tv },
  { id: 'talk-the-talk', label: 'Talk the Talk', description: 'Know the terminology and hold your own in conversations', icon: MessageCircle },
  { id: 'coach-play', label: 'Coach / Play', description: 'Learn schemes, play design, and game planning', icon: ClipboardList },
];

function getLevel(score: number): { level: string; label: string; description: string } {
  // Max score is 10 (5 questions × 2 points each)
  if (score <= 2) return { level: 'beginner', label: 'Rookie', description: 'No worries — we\'ll start from the very basics!' };
  if (score <= 6) return { level: 'intermediate', label: 'Fan', description: 'You know the game — let\'s deepen your knowledge!' };
  return { level: 'advanced', label: 'Student of the Game', description: 'Impressive! We\'ll focus on advanced schemes and strategy.' };
}

interface OnboardingFlowProps {
  onComplete: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('welcome');
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [customGoal, setCustomGoal] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>('basics');

  // Adaptive baseball quiz: list grows as the user answers; each next
  // question's tier shifts based on the previous result.
  const [baseballQuiz, setBaseballQuiz] = useState<QuizQuestion[]>(() => [BASEBALL_POOL.easy[0]]);
  const [baseballTier, setBaseballTier] = useState<Tier>('easy');
  const [baseballPrevTier, setBaseballPrevTier] = useState<Tier | null>(null);
  const [baseballUsed, setBaseballUsed] = useState<Set<string>>(() => new Set([BASEBALL_POOL.easy[0].question]));

  const tierRank: Record<Tier, number> = { easy: 0, medium: 1, hard: 2 };
  const tierShift: 'up' | 'down' | 'same' | null = baseballPrevTier
    ? tierRank[baseballTier] > tierRank[baseballPrevTier]
      ? 'up'
      : tierRank[baseballTier] < tierRank[baseballPrevTier]
        ? 'down'
        : 'same'
    : null;

  const pickBaseballQuestion = (tier: Tier, used: Set<string>): QuizQuestion => {
    const order: Tier[] = [
      tier,
      tier === 'easy' ? 'medium' : tier === 'hard' ? 'medium' : 'easy',
      tier === 'easy' ? 'hard' : 'easy',
    ];
    for (const t of order) {
      const candidate = BASEBALL_POOL[t].find(q => !used.has(q.question));
      if (candidate) return candidate;
    }
    const all = [...BASEBALL_POOL.easy, ...BASEBALL_POOL.medium, ...BASEBALL_POOL.hard];
    return all.find(q => !used.has(q.question)) ?? all[0];
  };

  const quizQuestions =
    selectedSport === 'soccer' ? SOCCER_QUESTIONS
    : selectedSport === 'basketball' ? BASKETBALL_QUESTIONS
    : selectedSport === 'baseball' ? baseballQuiz
    : FOOTBALL_QUESTIONS;
  const totalScore = quizAnswers.reduce((s, a) => s + a, 0);
  const levelInfo = getLevel(totalScore);

  const handleQuizAnswer = (points: number) => {
    const newAnswers = [...quizAnswers, points];
    setQuizAnswers(newAnswers);

    if (selectedSport === 'baseball') {
      if (newAnswers.length < BASEBALL_QUIZ_LENGTH) {
        const nextTier = nextBaseballTier(baseballTier, points);
        const nextQ = pickBaseballQuestion(nextTier, baseballUsed);
        setBaseballPrevTier(baseballTier);
        setBaseballTier(nextTier);
        setBaseballUsed(prev => new Set(prev).add(nextQ.question));
        setBaseballQuiz(prev => [...prev, nextQ]);
        setQuizIndex(i => i + 1);
      } else {
        setTimeout(() => setStep('goals'), 0);
      }
      return;
    }

    if (quizIndex < quizQuestions.length - 1) {
      setQuizIndex(i => i + 1);
    } else {
      setTimeout(() => setStep('goals'), 0);
    }
  };

  const toggleGoal = (id: string) => {
    setSelectedGoals(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const handleFinish = async () => {
    if (!user) return;
    setStep('saving');

    const goals = [...selectedGoals];
    const trimmedCustom = customGoal.trim().slice(0, 200);
    if (trimmedCustom) goals.push(`custom:${trimmedCustom}`);

    const knowledgeLevel = experienceMode === 'improve' 
      ? (levelInfo.level === 'beginner' ? 'intermediate' : levelInfo.level) 
      : levelInfo.level;

    const { error } = await supabase
      .from('profiles')
      .update({
        knowledge_level: knowledgeLevel,
        learning_goals: goals,
        preferred_sport: selectedSport,
        onboarding_completed: true,
        user_persona: selectedPersona,
      } as any)
      .eq('user_id', user.id);

    if (error) {
      toast.error('Failed to save — please try again');
      setStep('goals');
      return;
    }

    toast.success(`Welcome aboard, ${levelInfo.label}!`);
    onComplete();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-secondary text-secondary-foreground py-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Logo className="w-8 h-8" alt="Sportara" />
          <h1 className="font-display font-bold text-xl tracking-tight">
            Sportara
          </h1>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 py-4">
        {['welcome', 'sport', 'experience', 'persona', 'quiz', 'goals'].map((s, i) => {
          const stepOrder = ['welcome', 'sport', 'experience', 'persona', 'quiz', 'goals', 'saving'];
          const currentIdx = stepOrder.indexOf(step);
          return (
            <div
              key={s}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                currentIdx >= i ? 'bg-primary' : 'bg-border'
              }`}
            />
          );
        })}
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-sm">
          <AnimatePresence mode="wait">
            {step === 'welcome' && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center space-y-6"
              >
                <Logo className="w-16 h-16 mx-auto" />
                <h2 className="font-display font-bold text-2xl">Let's personalize your experience</h2>
                <p className="text-muted-foreground font-body text-sm leading-relaxed">
                  A few quick questions so we can match your starting point — whether you're brand new or already a fan. Takes about 30 seconds!
                </p>
                <Button
                  onClick={() => setStep('sport')}
                  className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                >
                  Let's Go <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <button
                  onClick={async () => {
                    if (!user) return;
                    await supabase
                      .from('profiles')
                      .update({ onboarding_completed: true } as any)
                      .eq('user_id', user.id);
                    onComplete();
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors font-body"
                >
                  Skip for now
                </button>
              </motion.div>
            )}

            {step === 'sport' && (
              <motion.div
                key="sport"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-5"
              >
                <div className="text-center">
                  <h2 className="font-display font-extrabold text-2xl">Pick your sport</h2>
                  <p className="text-muted-foreground font-body text-sm mt-2">
                    We'll tailor your experience. You can explore other sports anytime.
                  </p>
                </div>

                <div className="space-y-2.5">
                  {SPORT_OPTIONS.map(sport => {
                    const selected = selectedSport === sport.id;
                    return (
                      <motion.button
                        key={sport.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedSport(sport.id)}
                        className={`w-full p-4 rounded-xl border-2 text-left font-body transition-all flex items-center gap-3 ${
                          selected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/30'
                        }`}
                      >
                        <span className="text-3xl">{sport.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-sm">{sport.label}</p>
                          <p className="text-xs text-muted-foreground">{sport.description}</p>
                        </div>
                        {selected && (
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary-foreground" />
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <Button
                  onClick={() => setStep('experience')}
                  className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                >
                  Continue <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}

            {step === 'experience' && (
              <motion.div
                key="experience"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <ExperienceToggle
                  mode={experienceMode}
                  onChange={setExperienceMode}
                />

                <Button
                  onClick={() => setStep('persona')}
                  className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                >
                  Continue <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}

            {step === 'persona' && (
              <motion.div
                key="persona"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-5"
              >
                <div className="text-center">
                  <h2 className="font-display font-extrabold text-2xl">Who are you?</h2>
                  <p className="text-muted-foreground font-body text-sm mt-2">
                    This helps us tailor the content to your needs.
                  </p>
                </div>

                <div className="space-y-2.5">
                  {PERSONAS.map(persona => {
                    const selected = selectedPersona === persona.id;
                    return (
                      <motion.button
                        key={persona.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPersona(persona.id)}
                        className={`w-full p-4 rounded-xl border-2 text-left font-body transition-all flex items-center gap-3 ${
                          selected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/30'
                        }`}
                      >
                        <span className="text-3xl">{persona.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-sm">{persona.label}</p>
                          <p className="text-xs text-muted-foreground">{persona.description}</p>
                        </div>
                        {selected && (
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary-foreground" />
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <Button
                  onClick={() => setStep('quiz')}
                  disabled={!selectedPersona}
                  className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl disabled:opacity-50"
                >
                  Continue <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}

            {step === 'quiz' && (
              <motion.div
                key={`quiz-${quizIndex}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.25 }}
                className="space-y-5"
              >
                <div className="text-center flex items-center justify-center gap-2 flex-wrap">
                  <span className="inline-block bg-primary/10 text-primary font-display font-bold text-xs px-3 py-1 rounded-full">
                    QUESTION {quizIndex + 1} OF {quizQuestions.length}
                  </span>
                  {selectedSport === 'baseball' && (
                    <motion.span
                      key={baseballTier}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className={`inline-flex items-center gap-1 font-display font-bold text-xs px-3 py-1 rounded-full border ${
                        baseballTier === 'easy'
                          ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                          : baseballTier === 'medium'
                            ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
                            : 'bg-rose-500/10 text-rose-500 border-rose-500/30'
                      }`}
                      aria-label={`Difficulty: ${baseballTier}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {baseballTier === 'easy' ? 'EASY' : baseballTier === 'medium' ? 'MEDIUM' : 'HARD'}
                    </motion.span>
                  )}
                </div>
                {selectedSport === 'baseball' && tierShift && (
                  <motion.p
                    key={`shift-${quizIndex}`}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="text-center text-xs font-body text-muted-foreground"
                    aria-live="polite"
                  >
                    {tierShift === 'up' && (
                      <span className="text-emerald-500 font-semibold">↑ Stepping up</span>
                    )}
                    {tierShift === 'down' && (
                      <span className="text-amber-500 font-semibold">↓ Easing off</span>
                    )}
                    {tierShift === 'same' && <span>→ Holding steady</span>}
                    {' '}— next: {baseballTier}
                  </motion.p>
                )}


                {/* Progress bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    animate={{ width: `${((quizIndex) / quizQuestions.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                <p className="font-body font-semibold text-lg text-center leading-snug">
                  {quizQuestions[quizIndex].question}
                </p>

                <div className="space-y-2.5">
                  {quizQuestions[quizIndex].options.map((option, i) => (
                    <motion.button
                      key={i}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleQuizAnswer(option.points)}
                      className="w-full p-4 rounded-xl border-2 border-border bg-card text-left font-body font-medium transition-all hover:border-primary/50"
                    >
                      {option.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 'goals' && (
              <motion.div
                key="goals"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-5"
              >
                {/* Level result */}
                <div className="bg-card rounded-2xl border-2 border-primary/30 p-5 text-center shadow-card">
                  <div className="text-4xl mb-2">
                    {levelInfo.level === 'beginner' ? '🌱' : levelInfo.level === 'intermediate' ? '⭐' : '🧠'}
                  </div>
                  <p className="font-display font-extrabold text-xl text-primary">{levelInfo.label}</p>
                  <p className="text-sm text-muted-foreground font-body mt-1">{levelInfo.description}</p>
                </div>

                <h3 className="font-display font-bold text-lg text-center">What are you here to do?</h3>
                <p className="text-xs text-muted-foreground text-center font-body">Select all that apply</p>

                <div className="space-y-2.5">
                  {GOALS.map(goal => {
                    const selected = selectedGoals.includes(goal.id);
                    const Icon = goal.icon;
                    return (
                      <motion.button
                        key={goal.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleGoal(goal.id)}
                        className={`w-full p-4 rounded-xl border-2 text-left font-body transition-all flex items-center gap-3 ${
                          selected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/30'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selected ? 'bg-primary/15' : 'bg-muted'
                        }`}>
                          <Icon className={`w-5 h-5 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-sm">{goal.label}</p>
                          <p className="text-xs text-muted-foreground truncate">{goal.description}</p>
                        </div>
                        {selected && (
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary-foreground" />
                          </div>
                        )}
                      </motion.button>
                    );
                  })}

                  {/* Custom goal */}
                  {!showCustomInput ? (
                    <button
                      onClick={() => setShowCustomInput(true)}
                      className="w-full p-3 rounded-xl border-2 border-dashed border-border text-left font-body text-sm text-muted-foreground hover:border-primary/30 transition-all flex items-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      Something else...
                    </button>
                  ) : (
                    <div className="p-3 rounded-xl border-2 border-primary/30 bg-card">
                      <input
                        type="text"
                        value={customGoal}
                        onChange={e => setCustomGoal(e.target.value)}
                        maxLength={200}
                        placeholder="What else do you want to learn?"
                        className="w-full bg-transparent font-body text-sm outline-none placeholder:text-muted-foreground"
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleFinish}
                  disabled={selectedGoals.length === 0 && !customGoal.trim()}
                  className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl disabled:opacity-50"
                >
                  Start Learning <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}

            {step === 'saving' && (
              <motion.div
                key="saving"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center space-y-4"
              >
                <Logo className="w-12 h-12 animate-pulse mx-auto" />
                <p className="font-display font-bold text-lg text-muted-foreground">Building your training plan...</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
